import { Tab } from '@mui/material';
import { JSX, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from 'store/store';
import { getUserProfile } from 'store/user-service/asyncActions';

import { NamespacesList } from 'core/enums/common';
import { useGetUser } from 'core/hooks/useGetUser';

import { CustomTooltipElement } from '../shared/CustomDisabledElement/CustomTooltipElement';

import { Documents } from './Documents';
import { ModelSelection } from './ModelSelection';
import { Subscription } from './Subscription';
import { TabPanel } from './TabPanel';
import { User } from './User';
import * as Styled from './styled';

interface IProfileTabConfig {
	label: string;
	component: JSX.Element;
	styles: Record<string, string>;
	isSubscriptionOnly: boolean;
}

export const Profile = () => {
	const { t: profileTranslate } = useTranslation(NamespacesList['profilePage']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const dispatch = useAppDispatch();

	const [tabIndex, setTabIndex] = useState(0);
	const [profileTabsConfig, setProfileTabsConfig] = useState<Array<IProfileTabConfig>>([]);

	const { user } = useGetUser();

	const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
		setTabIndex(newValue);
	};

	useEffect(() => {
		const isPaymentEnabled = process.env?.REACT_APP_ENABLE_PAYMENTS
			? process.env.REACT_APP_ENABLE_PAYMENTS?.toLowerCase() === 'true'
			: true;

		const tabs = [];

		tabs.push({
			label: profileTranslate('text.settings'),
			component: <User />,
			styles: { maxWidth: '574px', margin: '0 auto' },
			isSubscriptionOnly: false,
		});

		if (isPaymentEnabled) {
			tabs.push({
				label: profileTranslate('text.subscription'),
				component: <Subscription />,
				styles: {},
				isSubscriptionOnly: false,
			});
		}

		tabs.push({
			label: profileTranslate('text.documents'),
			component: <Documents />,
			styles: {},
			isSubscriptionOnly: true,
		});

		tabs.push({
			label: profileTranslate('text.aiModel'),
			component: <ModelSelection />,
			styles: {},
			isSubscriptionOnly: true,
		});

		setProfileTabsConfig(tabs);

		dispatch(getUserProfile());
	}, []);

	const userHasSubscription = useMemo(
		() => user.accountPlan !== 0 && user.accountPlan !== null,
		[user.accountPlan, user.accountPlan]
	);

	return (
		<Styled.Profile>
			<Styled.TabsButtons value={tabIndex} onChange={handleChangeTab}>
				{profileTabsConfig.map((tab, index) =>
					!userHasSubscription && tab.isSubscriptionOnly ? (
						<CustomTooltipElement
							key={`tab page: ${index}`}
							message={commonTranslate('tooltips.unavailableForYourSubscriptionLevel')}
						>
							<Tab label={tab.label} sx={{ cursor: 'default' }} key={`tab: ${index}`} />
						</CustomTooltipElement>
					) : (
						<Tab label={tab.label} key={`tab: ${index}`} />
					)
				)}
			</Styled.TabsButtons>
			{profileTabsConfig.map((tab, index) => (
				<TabPanel key={`tab page: ${index}`} value={tabIndex} index={index}>
					<Styled.TabContent sx={tab.styles}>{tab.component}</Styled.TabContent>
				</TabPanel>
			))}
		</Styled.Profile>
	);
};
